"use client";

import { useState, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import { motion, AnimatePresence } from "framer-motion";
import {
  getCookieConsent,
  setCookieConsent,
  clearCookieConsent,
  type CookieConsent,
} from "@/lib/cookies";
import {
  getJurisdiction,
  readVisitorGeoCookie,
  requiresCookieConsent,
} from "@/lib/geo";

export function CookieBanner() {
  const t = useTranslations("ui.cookieBanner");
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [monitoring, setMonitoring] = useState(false);

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) {
      // Only show banner where legally required (GDPR/LGPD jurisdictions).
      // Read visitor_geo cookie set by proxy.ts (LGAL-01).
      const geo = readVisitorGeoCookie();
      if (geo && !requiresCookieConsent(getJurisdiction(geo))) return;
      // Small delay so the page loads first
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    setCookieConsent({ analytics: true, monitoring: true });
    setVisible(false);
  }, []);

  const handleRejectNonEssential = useCallback(() => {
    setCookieConsent({ analytics: false, monitoring: false });
    setVisible(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    setCookieConsent({ analytics, monitoring });
    setVisible(false);
  }, [analytics, monitoring]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-lg"
        >
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-[0_24px_48px_-12px_rgba(0,0,0,0.15)] border border-white/60 p-6">
            {!showDetails ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold mb-1">{t("title")}</h3>
                  <p
                    className="text-xs"
                    style={{ color: "var(--foreground-muted)" }}
                  >
                    {t("description")}{" "}
                    <a
                      href="https://help.alana.shopping/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                      style={{ color: "var(--alana-coral)" }}
                    >
                      {t("privacyPolicy")}
                    </a>
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleAcceptAll}
                    className="btn-primary text-xs !py-2 !px-4"
                  >
                    {t("acceptAll")}
                  </button>
                  <button
                    onClick={handleRejectNonEssential}
                    className="btn-secondary text-xs !py-2 !px-4"
                  >
                    {t("essentialOnly")}
                  </button>
                  <button
                    onClick={() => setShowDetails(true)}
                    className="text-xs underline cursor-pointer"
                    style={{ color: "var(--foreground-muted)" }}
                  >
                    {t("customize")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">
                    {t("preferencesTitle")}
                  </h3>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-xs underline cursor-pointer"
                    style={{ color: "var(--foreground-muted)" }}
                  >
                    {t("back")}
                  </button>
                </div>

                <div className="space-y-3">
                  <CategoryRow
                    label={t("essentialLabel")}
                    description={t("essentialDescription")}
                    checked={true}
                    disabled={true}
                  />
                  <CategoryRow
                    label={t("analyticsLabel")}
                    description={t("analyticsDescription")}
                    checked={analytics}
                    onChange={setAnalytics}
                  />
                  <CategoryRow
                    label={t("monitoringLabel")}
                    description={t("monitoringDescription")}
                    checked={monitoring}
                    onChange={setMonitoring}
                  />
                </div>

                <button
                  onClick={handleSavePreferences}
                  className="btn-primary text-xs !py-2 !px-4 w-full"
                >
                  {t("savePreferences")}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CategoryRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label
      className={`flex items-start gap-3 ${disabled ? "opacity-60" : "cursor-pointer"}`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-0.5 w-4 h-4 rounded accent-[#ff8086]"
      />
      <div>
        <span className="text-xs font-medium block">{label}</span>
        <span className="text-xs" style={{ color: "var(--foreground-muted)" }}>
          {description}
        </span>
      </div>
    </label>
  );
}

/**
 * Small button to re-open cookie preferences.
 * Can be placed in settings or footer.
 */
export function CookiePreferencesButton() {
  const t = useTranslations("ui.cookieBanner");
  const [consent, setConsent] = useState<CookieConsent | null>(null);

  useEffect(() => {
    setConsent(getCookieConsent());

    const handler = (e: Event) => {
      setConsent((e as CustomEvent).detail);
    };
    window.addEventListener("cookie-consent-change", handler);
    return () => window.removeEventListener("cookie-consent-change", handler);
  }, []);

  if (!consent) return null;

  const handleReset = () => {
    clearCookieConsent();
    window.location.reload();
  };

  return (
    <button
      onClick={handleReset}
      className="text-xs underline cursor-pointer"
      style={{ color: "var(--foreground-muted)" }}
    >
      {t("cookiePreferences")}
    </button>
  );
}
